(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.TROInquiry = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  const config = Object.freeze({ companyId: 'Wtn2qf', listId: 'XiR5WT', salesEmail: 'sales@therecoveryoutlet.com', revision: '2024-10-15', timeout: 12000 });
  const phone = value => {
    const digits = String(value || '').replace(/\D/g, '');
    if (digits.length === 10) return '+1' + digits;
    if (digits.length === 11 && digits[0] === '1') return '+' + digits;
    return null;
  };
  const escapeHTML = value => String(value ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const validEmail = value => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
  function createClient(fetcher = (...args) => fetch(...args), options = {}) {
    const settings = { ...config, ...options };
    async function post(path, payload) {
      if (!['events', 'subscriptions'].includes(path)) throw new Error('Unsupported client operation');
      const controller = new AbortController();
      let timer;
      const timeout = new Promise((_, reject) => {
        timer = setTimeout(() => {
          const error = new Error('Request outcome unconfirmed');
          error.code = 'TRO_REQUEST_UNCERTAIN';
          reject(error);
          controller.abort();
        }, settings.timeout);
      });
      try {
        const response = await Promise.race([Promise.resolve().then(() => fetcher('https://a.klaviyo.com/client/' + path + '/?company_id=' + settings.companyId, {
          method: 'POST', headers: { 'Content-Type': 'application/json', revision: settings.revision }, body: JSON.stringify(payload), signal: controller.signal
        })), timeout]);
        if (!response.ok) {
          const error = new Error('Provider did not accept the request');
          error.code = response.status === 408 || response.status >= 500 ? 'TRO_REQUEST_UNCERTAIN' : 'TRO_REQUEST_REJECTED';
          throw error;
        }
        return response;
      } catch (error) {
        if (!error.code) error.code = 'TRO_REQUEST_UNCERTAIN';
        throw error;
      } finally { clearTimeout(timer); }
    }
    const profile = lead => {
      const attributes = { email: String(lead.email || '').trim() };
      if (lead.first) attributes.first_name = lead.first;
      if (lead.last) attributes.last_name = lead.last;
      if (phone(lead.phone)) attributes.phone_number = phone(lead.phone);
      return attributes;
    };
    const event = (name, properties, lead) => post('events', { data: { type: 'event', attributes: {
      properties, metric: { data: { type: 'metric', attributes: { name } } }, profile: { data: { type: 'profile', attributes: profile(lead) } }
    } } });
    const subscribe = (lead, source, properties) => post('subscriptions', { data: { type: 'subscription', attributes: {
      custom_source: source, profile: { data: { type: 'profile', attributes: { ...profile(lead), properties } } }
    }, relationships: { list: { data: { type: 'list', id: settings.listId } } } } });
    const notify = properties => event('Lead Alert', properties, { email: settings.salesEmail });
    async function submit({ lead, properties, alertProperties, marketingConsent = false, source, requestId, metric = 'Requested Consultation' }) {
      const correlated = { ...properties, 'Request ID': requestId };
      try { await event(metric, correlated, lead); }
      catch (error) { return { state: error.code === 'TRO_REQUEST_REJECTED' ? 'rejected' : 'uncertain' }; }
      const outcomes = await Promise.allSettled([
        notify({ ...alertProperties, request_id: requestId }),
        marketingConsent ? subscribe(lead, source, correlated) : Promise.resolve(null)
      ]);
      return { state: outcomes[0].status === 'fulfilled' ? 'accepted' : 'accepted_alert_unconfirmed', newsletter: marketingConsent ? (outcomes[1].status === 'fulfilled' ? 'accepted' : 'unconfirmed') : 'not_requested' };
    }
    return { post, event, subscribe, notify, submit };
  }
  const client = createClient();
  function mount(options) {
    const form = document.getElementById(options.formId);
    if (!form || form.dataset.troInquiryMounted) return;
    form.dataset.troInquiryMounted = 'true';
    const text = JSON.parse(document.getElementById('tro-inquiry-copy').textContent);
    const button = document.getElementById(options.buttonId);
    const errorBox = document.getElementById('tro-form-error');
    errorBox.setAttribute('role', 'status'); errorBox.setAttribute('aria-live', 'polite'); errorBox.tabIndex = -1;
    const consentLabel = document.createElement('label'); consentLabel.className = 'tro-consent';
    const consent = document.createElement('input'); consent.type = 'checkbox'; consent.name = 'marketing_consent';
    consentLabel.append(consent, document.createTextNode(text.marketing));
    button.before(consentLabel);
    const field = key => document.getElementById(options.fields[key]);
    const value = key => field(key)?.value.trim() || '';
    let state = 'idle';
    let requestId;
    const message = value => { errorBox.textContent = value; errorBox.style.display = 'block'; errorBox.focus(); };
    const fallback = () => {
      const links = document.createElement('p');
      const call = document.createElement('a'); call.href = 'tel:18003983770'; call.textContent = '1 (800) 398-3770';
      const email = document.createElement('a'); email.href = 'mailto:' + config.salesEmail; email.textContent = config.salesEmail;
      links.append(call, document.createTextNode(' · '), email); return links;
    };
    form.addEventListener('submit', async event => {
      event.preventDefault();
      if (state !== 'idle' && state !== 'rejected') return;
      const invalid = [];
      for (const key of ['first', 'email', ...(options.phoneRequired ? ['phone'] : [])]) {
        const ok = key === 'email' ? validEmail(value(key)) : Boolean(value(key));
        field(key).setAttribute('aria-invalid', String(!ok));
        field(key).classList.toggle('tro-invalid', !ok);
        if (!ok) invalid.push(field(key));
      }
      if (invalid.length) { message(text.invalid); invalid[0].focus(); return; }
      state = 'sending'; button.disabled = true; button.textContent = text.sending;
      form.setAttribute('aria-busy', 'true'); errorBox.style.display = 'none';
      requestId = requestId || crypto.randomUUID();
      const lead = { first: value('first'), last: value('last'), email: value('email'), phone: value('phone') };
      const properties = { 'Lead Type': options.kind, ...Object.fromEntries(Object.entries(options.properties).map(([name, id]) => [name, document.getElementById(id)?.value.trim() || ''])), ...(options.extra?.() || {}) };
      const alertProperties = { ...properties, source: options.source, lead_name: [lead.first, lead.last].filter(Boolean).join(' '), lead_email: lead.email, lead_phone: lead.phone,
        lead_type: options.kind, facility_name: properties['Facility Name'] || '', facility_type: properties['Facility Type'] || '', equipment_interest: properties['Equipment Interest'] || '',
        primary_interest: properties['Primary Interest'] || properties['Equipment Interest'] || '', budget_range: properties['Budget Range'] || '', timeline: properties.Timeline || '', space: properties.Space || '', notes: properties.Notes || '', ...(options.alertExtra?.() || {}) };
      const result = await client.submit({ lead, properties, alertProperties, marketingConsent: consent.checked, source: options.source, requestId });
      state = result.state; form.setAttribute('aria-busy', 'false');
      if (state === 'rejected') { button.disabled = false; button.textContent = text.retry; message(text.rejected); return; }
      button.disabled = true;
      button.textContent = state === 'uncertain' ? text.unconfirmed : text.submitted;
      if (state === 'uncertain' || state === 'accepted_alert_unconfirmed') {
        message(state === 'uncertain' ? text.uncertain : text.alert_unconfirmed); errorBox.append(fallback()); return;
      }
      const card = document.getElementById('tro-form-card');
      const panel = document.createElement('div'); panel.className = 'tro-inquiry'; panel.tabIndex = -1;
      const heading = document.createElement('h3'); heading.textContent = text.submitted;
      const note = document.createElement('p'); note.textContent = text.accepted;
      panel.append(heading, note, fallback());
      if (result.newsletter === 'unconfirmed') { const n = document.createElement('p'); n.textContent = text.newsletter_unconfirmed; panel.append(n); }
      const preference = document.createElement('form');
      const label = document.createElement('label'); label.htmlFor = 'tro-preferred-time'; label.textContent = text.preference;
      const input = document.createElement('input'); input.id = 'tro-preferred-time'; input.type = 'datetime-local'; input.required = true;
      const help = document.createElement('p'); help.id = 'tro-time-help'; help.textContent = text.time_help; input.setAttribute('aria-describedby', help.id);
      const send = document.createElement('button'); send.type = 'submit'; send.textContent = text.request_time;
      const status = document.createElement('p'); status.setAttribute('role', 'status'); status.tabIndex = -1;
      preference.append(label, input, help, send, status); panel.append(preference);
      let timeState = 'idle';
      preference.addEventListener('submit', async event => {
        event.preventDefault(); if (timeState !== 'idle' && timeState !== 'rejected') return;
        if (!input.reportValidity()) return;
        timeState = 'sending'; send.disabled = true; send.textContent = text.sending;
        const when = input.value + ' America/New_York (preferred local time)';
        const result = await client.submit({ lead, properties: { ...properties, 'Preferred Time': when, 'Time Zone': 'America/New_York' }, alertProperties: { ...alertProperties, preferred_time: when }, source: options.source, requestId: requestId + '-time', metric: 'Consultation Time Requested' });
        timeState = result.state; send.disabled = timeState !== 'rejected'; send.textContent = timeState === 'rejected' ? text.retry : text.submitted;
        status.textContent = timeState === 'accepted' ? text.time_accepted : timeState === 'rejected' ? text.rejected : text.uncertain;
        status.focus();
      });
      card.replaceChildren(panel); panel.focus();
    });
  }
  return { createClient, client, mount, escapeHTML, validEmail, phone };
});
